import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import cls from 'classnames';
import { usePopper } from 'react-popper';
import { TicketHorizontalIcon, PersonLoginIcon } from '@bizzabo/icons';

import BZReactOutsideClickHandler from 'web-common/bizzabo-components/components/BZReactOutsideClickHandler';

import { sendAnalytics } from 'utils/analyticsUtils';
import { isMobile } from './sessionBroadcastUtils';
import { notifyRedirectToTicketWidget } from './LoginPopover';
import { usePrevious } from '../../../utils/ReactHooks';
import {
    sendCustomClick
} from '../../../utils/SnowplowUtils';
import {
    buttonTypes,
    loginSubCategory,
    registerSubCategory
} from '../../../constants/SnowplowConst';

const RegisterAndLoginPopper = ({
    eventName = '',
    analyticData,
    onLoginClick,
    eventUrls: { registrationsUrl },
    button,
    isWebLoginV2Loading
}) => {
    const [referenceElement, setReferenceElement] = React.useState(null);
    const [popperElement, setPopperElement] = React.useState(null);
    const [arrowElement, setArrowElement] = React.useState(null);
    const [isPopperVisible, setPopperVisibility] = React.useState(false);

    const prevLoadingState = usePrevious(isWebLoginV2Loading);

    const loginButtonRef = useRef();
    const registerButtonRef = useRef();

    useEffect(() => {
        const shouldHidePopper = isPopperVisible && !isWebLoginV2Loading && prevLoadingState;
        if (shouldHidePopper) {
            setPopperVisibility(false);
        }
    }, [isPopperVisible, isWebLoginV2Loading, prevLoadingState]);

    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: isMobile() ? 'bottom' : 'right',
        modifiers: [
            {
                name: 'arrow',
                options: { element: arrowElement }
            },
            {
                name: 'flip',
                options: {
                    fallbackPlacements: ['bottom', 'top']
                }
            },
            {
                name: 'offset',
                options: {
                    offset: [10, 10],
                },
            },
        ]
    });

    const handleLogin = () => {
        sendAnalytics('loginClick', analyticData);
        sendCustomClick(buttonTypes.login, loginSubCategory.loginTooltipNewFlow, loginButtonRef);
        onLoginClick && onLoginClick();
    };

    const handleRegister = () => {
        sendCustomClick(buttonTypes.register, registerSubCategory.registerTooltipNewFlow, registerButtonRef);
        notifyRedirectToTicketWidget();
        setPopperVisibility(false);
    };

    const loginBtnClassName = cls('cta-button cta-button-large font-size-13 custom-color color-1-background  margin-top-small color-1-hover-opacity', {
        'with-spinner': isWebLoginV2Loading
    });

    return (
        <>
            <div
                ref={setReferenceElement}
                onClick={() => setPopperVisibility(true)}
                className="reference-wrapper"
            >
                {button}
            </div>

            {isPopperVisible &&
                ReactDOM.createPortal(
                    <BZReactOutsideClickHandler
                        onOutsideClick={() => setPopperVisibility(false)}
                    >
                        <div
                            ref={setPopperElement}
                            style={styles.popper}
                            className="register-login-popper-content text-center"
                            {...attributes.popper}
                        >
                            <div
                                ref={setArrowElement}
                                style={styles.arrow}
                                className="bz-popper-arrow"
                                data-popper-arrow
                            />
                            <h4 className="title font-size-20">
                                Welcome to {eventName}
                            </h4>
                            <div className="subtitle font-size-14">
                                You must be logged in and registered to
                                personalize your agenda, save seats for
                                sessions, access virtual sessions and more!
                            </div>
                            <div className="inner-container">
                                <div className="text-center">
                                    <TicketHorizontalIcon
                                        color="#32333C"
                                        fontSize={40}
                                        className="display-block margin-auto"
                                    />
                                    <strong>Not registered yet?</strong>
                                    <div className="margin-bottom-xs">
                                        Get registered to access the full
                                        content
                                    </div>
                                    <a
                                        ref={registerButtonRef}
                                        href={registrationsUrl}
                                        onClick={handleRegister}
                                        className="cta-button cta-button-large font-size-13 custom-color color-1 color-1-border margin-top-small color-1-hover-opacity-outline"
                                        target="_top"
                                    >
                                        Register
                                    </a>
                                </div>
                                <div className="text-center login-container">
                                    <PersonLoginIcon
                                        color="#32333C"
                                        fontSize={40}
                                        className="display-block margin-auto"
                                    />
                                    <strong>
                                        Already registered for the event?
                                    </strong>
                                    <div className="margin-bottom-xs">
                                        Simply log in to get the full experience
                                    </div>
                                    <button
                                        ref={loginButtonRef}
                                        onClick={handleLogin}
                                        className={loginBtnClassName}
                                    >
                                        {isWebLoginV2Loading ? <i className="fa fa-spinner fa-spin font-size-24"/> : 'Log in'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </BZReactOutsideClickHandler>,
                    document.querySelector('.main-react-view')
                )}
        </>
    );
};

export default RegisterAndLoginPopper;
